/**
 * ブラウザ側で PDF を pdf.js で Canvas に描画し、ページ単位の PNG Blob に変換する。
 * Cloudflare Workers は PDF ライブラリを実行できないため、変換はブラウザ側で完結させる方針
 * (docs/design/v02-ai-ocr-web-integration.md §3-1)。
 *
 * scale=1.5 / concurrency=4 は同ドキュメント §8 の実測 PoC (麻生.pdf 33ページ) で確定した値。
 * scale=2.0 でも処理時間は同等だが PNG サイズが63%増になるだけなので不採用。
 */
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const SCALE = 1.5;
const CONCURRENCY = 4;

export type PdfPageImage = { pageNumber: number; blob: Blob };

export const convertPdfToPngPages = async (
  file: File,
  onProgress?: (done: number, total: number) => void,
): Promise<PdfPageImage[]> => {
  const buf = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: buf });
  const pdf = await loadingTask.promise;
  const total = pdf.numPages;
  const results: PdfPageImage[] = new Array(total);
  let done = 0;
  let cursor = 0;

  const renderOne = async (pageNumber: number): Promise<void> => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: SCALE });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const canvasContext = canvas.getContext('2d');
    if (!canvasContext) throw new Error('Canvas 2D コンテキストの取得に失敗しました');
    // pdfjs v6 は canvas の明示渡しが必要 (canvasContext だけでは不足、PoC実測で確認済み)
    await page.render({ canvasContext, viewport, canvas } as Parameters<typeof page.render>[0]).promise;
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG変換に失敗しました'))), 'image/png');
    });
    page.cleanup();
    results[pageNumber - 1] = { pageNumber, blob };
  };

  const workers = Array.from({ length: Math.min(CONCURRENCY, total) }, async () => {
    while (cursor < total) {
      cursor += 1;
      const pageNumber = cursor;
      await renderOne(pageNumber);
      done += 1;
      onProgress?.(done, total);
    }
  });
  await Promise.all(workers);
  await loadingTask.destroy();
  return results;
};
